import * as React from 'react';

import {motion} from 'framer-motion';

import {getUserPrefersReducedMotion} from 'nutshell-core/utils/get-user-prefers-reduced-motion';

type Props = {
    children: React.ReactNode | null | undefined;
    initial?: {
        [key: string]: any;
    };
    animate?: {
        [key: string]: any;
    };
    transition?: {
        [key: string]: any;
    };
    index?: number;
    threshold?: number;
    rootMargin?: string;
    // Whether to trigger the animation only once or every time the element
    // becomes visible again
    once?: boolean;
};

export function ScrollTriggeredAnimateIn(props: Props) {
    const {
        children,
        initial = {opacity: 0, y: 20},
        animate = {opacity: 1, y: 0},
        transition = {duration: 0.6, ease: 'easeInOut'},
        index = 0,
        threshold = 0,
        rootMargin = '50px 0px -50px 0px',
        once = true,
    } = props;

    const ref = React.useRef<HTMLElement | null | undefined>(null);
    const [isVisible, setIsVisible] = React.useState<boolean | null | undefined>(false);

    React.useEffect(() => {
        if (!children) {
            return;
        }

        const observer = new IntersectionObserver(
            // @ts-expect-error
            ([entry]: [any]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    if (once) {
                        observer.disconnect();
                    }
                } else if (!once) {
                    setIsVisible(false);
                }
            },
            {threshold, rootMargin}
        );

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => observer.disconnect();
    }, [threshold, rootMargin, once, children]);

    if (!children) {
        return null;
    }

    const staggeredTransition = {
        ...transition,
        delay: transition.delay || index * 0.1,
    } as const;

    if (getUserPrefersReducedMotion()) {
        return children;
    }

    return (
        <motion.div
            // @ts-expect-error
            ref={ref}
            initial={initial}
            animate={isVisible ? animate : initial}
            transition={staggeredTransition}
        >
            {children}
        </motion.div>
    );
}
